import React, { useState } from 'react';
import {
   Box,
   Grid,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   Typography,
   IconButton,
   Button,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Tooltip,
   Chip,
   Menu,
   MenuItem,
   ListItemIcon,
   ListItemText,
   Divider,
   Slider,
   Snackbar,
   Alert,
   AlertColor,
   CircularProgress,
} from '@mui/material';
import {
   Share as ShareIcon,
   Download as DownloadIcon,
   Delete as DeleteIcon,
   Facebook as FacebookIcon,
   Twitter as TwitterIcon,
   LinkedIn as LinkedInIcon,
   Instagram as InstagramIcon,
   Email as EmailIcon,
   WhatsApp as WhatsAppIcon,
   ContentCopy as CopyLinkIcon,
   MoreVert as MoreIcon,
   CompareArrows as CompareIcon,
   Fullscreen as FullscreenIcon,
   ArrowForwardIos,
   ArrowBackIos,
   Save as SaveIcon,
} from '@mui/icons-material';
import { TransformedPhoto } from '../types';
import Carousel from 'react-material-ui-carousel';
// Interface for the component props
interface TransformedGalleryProps {
   photos: TransformedPhoto[];
   fullGallery?: boolean;
   onViewAll?: () => void;
   onDelete?: (photoId: string) => void;
   originalPhoto?: any;
   savePhoto: (photo: TransformedPhoto) => Promise<void>;
}

const TransformedGallery: React.FC<TransformedGalleryProps> = ({
   photos,
   fullGallery = false,
   onViewAll,
   onDelete,
   originalPhoto,
   savePhoto,
}) => {
   // State management
   const [selectedPhoto, setSelectedPhoto] = useState<TransformedPhoto | null>(null);
   const [detailDialogOpen, setDetailDialogOpen] = useState(false);
   const [beforeAfterDialogOpen, setBeforeAfterDialogOpen] = useState(false);
   const [shareMenuAnchorEl, setShareMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [activePhotoId, setActivePhotoId] = useState<string | null>(null);

   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState('info');
   const [snackbarOpen, setSnackbarOpen] = useState(false);

   // Handle opening the detail dialog
   const handleOpenDetailDialog = (photo: TransformedPhoto) => {
      setSelectedPhoto(photo);
      setDetailDialogOpen(true);
   };

   // Handle opening the before/after comparison dialog
   const handleOpenBeforeAfterDialog = (photo: TransformedPhoto) => {
      setSelectedPhoto(photo);
      setBeforeAfterDialogOpen(true);
   };

   // Handle opening the share menu
   const handleOpenShareMenu = (event: React.MouseEvent<HTMLButtonElement>, photoId: string) => {
      setShareMenuAnchorEl(event.currentTarget);
      setActivePhotoId(photoId);
   };

   // Handle closing the share menu
   const handleCloseShareMenu = () => {
      setShareMenuAnchorEl(null);
      setActivePhotoId(null);
   };

   // Handle opening the options menu
   const handleOpenOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>, photoId: string) => {
      setOptionsMenuAnchorEl(event.currentTarget);
      setActivePhotoId(photoId);
   };

   // Handle closing the options menu
   const handleCloseOptionsMenu = () => {
      setOptionsMenuAnchorEl(null);
      setActivePhotoId(null);
   };

   // Handle sharing on a platform
   const handleShare = (url: string) => {
      navigator.clipboard.writeText(url);
      setSnackbarMessage('Link copied to clipboard');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      handleCloseShareMenu();
   };

   // Handle downloading a photo
   const handleDownload = async (url: string) => {
      try {
         const response = await fetch(url);
         const blob = await response.blob();
         const blobUrl = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = blobUrl;
         // Get filename from URL or use a default name
         const filename = url.split('/').pop() || 'transformed-image.jpg';
         link.download = filename;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(blobUrl);

         setSnackbarMessage('Download started successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         setSnackbarMessage('Failed to download image');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   // Handle deleting a photo
   const handleDelete = (photoId: string) => {
      if (onDelete) {
         onDelete(photoId);
      }
      handleCloseOptionsMenu();
   };

   // Format date for display
   const formatDate = (date: Date) => {
      return new Date(date).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
      });
   };

   // Handle saving a photo
   const handleSave = async (photoId: string) => {
      try {
         const photo = photos.find((p) => p.id === photoId);

         if (photo) {
            await savePhoto(photo);
         }
      } catch (error) {}
   };

   return (
      <Box>
         {photos.length === 0 ? (
            <Typography variant='body2' color='text.secondary' align='center' sx={{ p: 3 }}>
               No transformed photos yet. Transform some photos to see them here.
            </Typography>
         ) : (
            <>
               <Grid container spacing={3}>
                  {photos.map((photo) => (
                     <TransformedPhotoCard
                        key={photo.id}
                        photo={photo}
                        fullGallery={fullGallery}
                        formatDate={formatDate}
                        handleOpenDetailDialog={handleOpenDetailDialog}
                        handleOpenShareMenu={handleOpenShareMenu}
                        handleDownload={handleDownload}
                        savePhoto={handleSave}
                        handleOpenBeforeAfterDialog={handleOpenBeforeAfterDialog}
                     />
                  ))}
               </Grid>

               {/* {!fullGallery && photos.length > 4 && onViewAll && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                     <Button variant='outlined' onClick={onViewAll}>
                        View All Transformations
                     </Button>
                  </Box>
               )} */}

               {/* Detail dialog */}
               <Dialog
                  open={detailDialogOpen}
                  onClose={() => setDetailDialogOpen(false)}
                  maxWidth='md'
                  fullWidth
               >
                  {selectedPhoto && (
                     <>
                        <DialogTitle>
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                              }}
                           >
                              <Typography variant='h6'>
                                 {selectedPhoto.stylePreset.charAt(0).toUpperCase() +
                                    selectedPhoto.stylePreset.slice(1).replace(/-/g, ' ')}{' '}
                                 Style Transformation
                              </Typography>
                              <Chip
                                 label={formatDate(selectedPhoto.createdAt)}
                                 size='small'
                                 variant='outlined'
                              />
                           </Box>
                        </DialogTitle>
                        <DialogContent dividers>
                           <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                              <img
                                 src={selectedPhoto?.transformedUrl}
                                 alt='Transformed Photo'
                                 style={{
                                    maxWidth: '100%',
                                    maxHeight: 'calc(80vh - 200px)',
                                    objectFit: 'contain',
                                 }}
                              />
                           </Box>
                           {selectedPhoto.prompt && (
                              <Box sx={{ mt: 2 }}>
                                 <Typography variant='subtitle2'>Custom Prompt:</Typography>
                                 <Typography variant='body2' color='text.secondary'>
                                    "{selectedPhoto.prompt}"
                                 </Typography>
                              </Box>
                           )}
                        </DialogContent>
                        <DialogActions>
                           <Button
                              startIcon={<SaveIcon />}
                              onClick={() => handleSave(selectedPhoto.id)}
                           >
                              Save
                           </Button>
                           <Button
                              startIcon={<CompareIcon />}
                              onClick={() => {
                                 setDetailDialogOpen(false);
                                 setBeforeAfterDialogOpen(true);
                              }}
                           >
                              Compare Before/After
                           </Button>
                           <Button
                              startIcon={<ShareIcon />}
                              onClick={(e) => handleOpenShareMenu(e, selectedPhoto.id)}
                           >
                              Share
                           </Button>
                           <Button
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownload(selectedPhoto?.transformedUrl || '')}
                           >
                              Download
                           </Button>
                           <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
                        </DialogActions>
                     </>
                  )}
               </Dialog>

               {/* Before/After comparison dialog */}
               <Dialog
                  open={beforeAfterDialogOpen}
                  onClose={() => setBeforeAfterDialogOpen(false)}
                  maxWidth='lg'
                  fullWidth
               >
                  {selectedPhoto && (
                     <>
                        <DialogTitle>Before & After Comparison</DialogTitle>
                        <DialogContent dividers>
                           <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                 <Typography variant='subtitle2' align='center' gutterBottom>
                                    Original Photo
                                 </Typography>
                                 <Box
                                    sx={{
                                       background: `url(${originalPhoto?.url || originalPhoto?.thumbnailUrl || ''}) no-repeat center center`,
                                       margin: 'auto',
                                       width: '100%',
                                       height: 400,
                                       display: 'flex',
                                       justifyContent: 'center',
                                       alignItems: 'center',
                                       border: '1px solid #eee',
                                       borderRadius: 1,
                                    }}
                                 >
                                    {/* This would fetch and display the original photo */}
                                    {/* <img
                                       src={originalPhoto?.url || originalPhoto?.thumbnailUrl || ''}
                                       alt='Original Photo'
                                       style={{
                                          width: `100%!`,
                                          height: 400,
                                          objectFit: 'scale-down',
                                       }}
                                    /> */}
                                 </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                 <Typography variant='subtitle2' align='center' gutterBottom>
                                    Transformed Photo
                                 </Typography>
                                 {/* make a image carousel of original photos */}
                                 <Carousel
                                    indicators={false}
                                    animation='slide'
                                    navButtonsAlwaysVisible={true}
                                    navButtonsProps={{
                                       style: {
                                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                          borderRadius: '50%',
                                          padding: '10px',
                                          color: 'white',
                                       },
                                    }}
                                    NextIcon={<ArrowForwardIos />}
                                    PrevIcon={<ArrowBackIos />}
                                    sx={{
                                       width: '100%',
                                       height: '400px',
                                       border: '1px solid #e0e0e0',
                                       borderRadius: '4px',
                                       '& .MuiPaper-root': {
                                          height: '100%',
                                       },
                                    }}
                                 >
                                    {photos.map((photo) => (
                                       <>
                                          <Box
                                             key={photo.id}
                                             sx={{
                                                background: `url(${photo.transformedUrl}) no-repeat center center`,
                                                position: 'relative!important',
                                                margin: 'auto',
                                                width: '100%',
                                                height: '400px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                             }}
                                          >
                                             <Tooltip title='Save to My Transformed Photos'>
                                                <IconButton
                                                   sx={{
                                                      position: 'absolute !important',
                                                      top: 10,
                                                      left: '50%',
                                                      zIndex: 50,
                                                      width: '20px',
                                                      height: '20px',
                                                      backgroundColor: 'green',
                                                      borderRadius: '50%',
                                                      '&:hover': {
                                                         backgroundColor: 'green!important',
                                                      },
                                                   }}
                                                   onClick={() => handleSave(photo.id)}
                                                >
                                                   <SaveIcon
                                                      sx={{
                                                         color: 'white',
                                                         width: '15px',
                                                         height: '15px',
                                                      }}
                                                      fontSize='small'
                                                   />
                                                </IconButton>
                                             </Tooltip>
                                             {/* <img
                                                src={photo.transformedUrl}
                                                alt='Transformed Photo'
                                                style={{
                                                   maxWidth: '100%',
                                                   maxHeight: '100%',
                                                   objectFit: 'contain',
                                                }}
                                             /> */}
                                          </Box>
                                       </>
                                    ))}
                                 </Carousel>
                              </Grid>
                           </Grid>
                        </DialogContent>
                        <DialogActions>
                           <Button onClick={() => setBeforeAfterDialogOpen(false)}>Close</Button>
                        </DialogActions>
                     </>
                  )}
               </Dialog>

               {/* Share menu */}
               <Menu
                  anchorEl={shareMenuAnchorEl}
                  open={Boolean(shareMenuAnchorEl)}
                  onClose={handleCloseShareMenu}
               >
                  {/* <MenuItem onClick={() => handleShare('facebook')}>
                     <ListItemIcon>
                        <FacebookIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Facebook</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('twitter')}>
                     <ListItemIcon>
                        <TwitterIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Twitter</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('linkedin')}>
                     <ListItemIcon>
                        <LinkedInIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>LinkedIn</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('instagram')}>
                     <ListItemIcon>
                        <InstagramIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Instagram</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleShare('email')}>
                     <ListItemIcon>
                        <EmailIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Email</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('whatsapp')}>
                     <ListItemIcon>
                        <WhatsAppIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>WhatsApp</ListItemText>
                  </MenuItem>
                  <Divider /> */}
                  <MenuItem onClick={() => handleShare(selectedPhoto?.transformedUrl || '')}>
                     <ListItemIcon>
                        <CopyLinkIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Copy Link</ListItemText>
                  </MenuItem>
               </Menu>

               {/* Options menu */}
               <Menu
                  anchorEl={optionsMenuAnchorEl}
                  open={Boolean(optionsMenuAnchorEl)}
                  onClose={handleCloseOptionsMenu}
               >
                  <MenuItem
                     onClick={() => {
                        const photo = photos.find((p) => p.id === activePhotoId);
                        if (photo) {
                           handleOpenDetailDialog(photo);
                           handleCloseOptionsMenu();
                        }
                     }}
                  >
                     <ListItemIcon>
                        <FullscreenIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>View Full Size</ListItemText>
                  </MenuItem>
                  <MenuItem
                     onClick={() => {
                        const photo = photos.find((p) => p.id === activePhotoId);
                        if (photo) {
                           handleOpenBeforeAfterDialog(photo);
                           handleCloseOptionsMenu();
                        }
                     }}
                  >
                     <ListItemIcon>
                        <CompareIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Compare Before/After</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => activePhotoId && handleDelete(activePhotoId)}>
                     <ListItemIcon>
                        <DeleteIcon fontSize='small' color='error' />
                     </ListItemIcon>
                     <ListItemText primaryTypographyProps={{ color: 'error' }}>
                        Delete Transformation
                     </ListItemText>
                  </MenuItem>
               </Menu>
            </>
         )}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity as AlertColor}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

const TransformedPhotoCard = ({
   photo,
   fullGallery,
   formatDate,
   handleOpenDetailDialog,
   handleOpenShareMenu,
   handleDownload,
   savePhoto,
   handleOpenBeforeAfterDialog,
}: {
   photo: TransformedPhoto;
   fullGallery: boolean;
   formatDate: (date: Date) => string;
   handleOpenDetailDialog: (photo: TransformedPhoto) => void;
   handleOpenShareMenu: (event: React.MouseEvent<HTMLButtonElement>, photoId: string) => void;
   handleDownload: (url: string) => void;
   savePhoto: (photoId: string) => void;
   handleOpenBeforeAfterDialog: (photo: TransformedPhoto) => void;
}) => {
   const [isSaving, setIsSaving] = useState(false);

   const handleSave = async (photoId: string) => {
      try {
         setIsSaving(true);
         await savePhoto(photoId);
         setIsSaving(false);
      } catch (error) {
         setIsSaving(false);
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <Grid
         item
         xs={12}
         sm={6}
         md={3}
         key={photo.id}
         // sx={{
         //    height: 300,
         //    width: 300,
         // }}
      >
         <Card
            sx={{
               // height: '100%',
               display: 'flex',
               // width: '100%',
               flexDirection: 'column',
            }}
         >
            {/* save button on the top right, make icon color to white backgroudn and green when saved */}
            {isSaving ? (
               <CircularProgress
                  size={20}
                  sx={{
                     position: 'absolute',
                     top: 10,
                     right: 10,
                  }}
               />
            ) : (
               <Tooltip title='Save to My Transformed Photos'>
                  <IconButton
                     sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'green',
                        borderRadius: '50%',
                        '&:hover': {
                           backgroundColor: 'green!important',
                        },
                     }}
                     onClick={() => handleSave(photo.id)}
                  >
                     <SaveIcon
                        sx={{
                           color: 'white',
                           width: '15px',
                           height: '15px',
                        }}
                        fontSize='small'
                     />
                  </IconButton>
               </Tooltip>
            )}
            <CardMedia
               component='img'
               height={fullGallery ? 180 : 220}
               image={photo.transformedUrl}
               alt={`Transformed photo ${photo.id}`}
               sx={{ cursor: 'pointer', objectFit: 'cover' }}
               onClick={() => handleOpenDetailDialog(photo)}
            />
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
               <Typography variant='caption' color='text.secondary' display='block'>
                  Created {formatDate(photo.createdAt)}
               </Typography>
            </CardContent>
            {/* display on hover */}
            <CardActions
               sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  justifyContent: 'space-between',
                  pt: 0,
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  opacity: 0,
                  '&:hover': {
                     opacity: 1,
                  },
               }}
            >
               <Box>
                  <Tooltip title='Share'>
                     <IconButton
                        sx={{ color: 'white' }}
                        size='small'
                        onClick={(e) => handleOpenShareMenu(e, photo.id)}
                        aria-label='share'
                     >
                        <ShareIcon fontSize='small' />
                     </IconButton>
                  </Tooltip>
                  <Tooltip title='Download'>
                     <IconButton
                        sx={{ color: 'white' }}
                        size='small'
                        onClick={() => handleDownload(photo.transformedUrl || '')}
                        aria-label='download'
                     >
                        <DownloadIcon fontSize='small' />
                     </IconButton>
                  </Tooltip>
                  <Tooltip title='Compare Before/After'>
                     <IconButton
                        sx={{ color: 'white' }}
                        size='small'
                        onClick={() => handleOpenBeforeAfterDialog(photo)}
                        aria-label='compare before and after'
                     >
                        <CompareIcon fontSize='small' />
                     </IconButton>
                  </Tooltip>
               </Box>
            </CardActions>
         </Card>
      </Grid>
   );
};




export default TransformedGallery;
